import React, { useEffect, useState } from "react";
import { Button, Typography, Card, Modal, Row, Col, Space } from "antd";
import { ReloadOutlined, DeleteOutlined } from "@ant-design/icons";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import {
  listCacheCategories,
  resetCache as resetCacheService,
} from "./cache-service";

const CacheView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await listCacheCategories(apiCall);

      if (response && response.items) {
        setCategories(response.items);
      } else {
        setCategories([]);
      }
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch cache categories");
      setLoading(false);
    }
  };

  const handleResetClick = category => {
    setSelectedCategory(category);
    setResetDialogOpen(true);
  };

  const handleResetConfirm = async () => {
    if (!selectedCategory) {
      return;
    }

    try {
      setResetting(true);
      const response = await resetCacheService(
        apiCall,
        selectedCategory.cluster,
        selectedCategory.prefix,
      );

      if (response && response.success) {
        successToast(`Cache reset successfully for ${selectedCategory.name}`);
        setResetDialogOpen(false);
        setSelectedCategory(null);
      } else {
        throw new Error("Reset failed");
      }
      setResetting(false);
    } catch (error) {
      errorToast("Failed to reset cache");
      setResetting(false);
    }
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
    setSelectedCategory(null);
  };

  return (
    <div style={{ flexGrow: 1, padding: 16 }}>
      <Row gutter={24}>
        {/* Header */}
        <Col span={24}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <Typography.Title level={3}>Cache Management</Typography.Title>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchCategories}
              disabled={loading}
            >
              Refresh
            </Button>
          </div>
        </Col>

        {/* Categories List */}
        <Col span={24}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
              <LoadingSpinner />
            </div>
          ) : (
            <Row gutter={16}>
              {categories.length === 0 ? (
                <Col span={24}>
                  <Typography.Text type="secondary" style={{ display: "block", textAlign: "center" }}>
                    No cache categories available
                  </Typography.Text>
                </Col>
              ) : (
                categories.map((category, index) => (
                  <Col xs={24} sm={12} md={8} key={index}>
                    <Card
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                      }}
                      bodyStyle={{ flex: 1, display: "flex", flexDirection: "column" }}
                    >
                      <div style={{ flex: 1 }}>
                        <Typography.Title level={5} style={{ marginBottom: 8 }}>
                          {category.name}
                        </Typography.Title>
                        <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                          Cluster: {category.cluster}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ display: "block" }}>
                          Prefix: {category.prefix}
                        </Typography.Text>
                      </div>
                      <div style={{ marginTop: 16 }}>
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          disabled={resetting}
                          onClick={() => handleResetClick(category)}
                          block
                        >
                          Reset
                        </Button>
                      </div>
                    </Card>
                  </Col>
                ))
              )}
            </Row>
          )}
        </Col>
      </Row>

      {/* Reset Confirmation Modal */}
      <Modal
        open={resetDialogOpen}
        onCancel={handleResetCancel}
        title="Confirm Cache Reset"
        footer={[
          <Button key="cancel" onClick={handleResetCancel} disabled={resetting}>
            Cancel
          </Button>,
          <Button
            key="reset"
            type="primary"
            danger
            onClick={handleResetConfirm}
            disabled={resetting}
            loading={resetting}
          >
            Reset
          </Button>,
        ]}
      >
        <Typography.Text>
          Are you sure you want to reset the cache for{" "}
          <strong>{selectedCategory?.name}</strong>? This action cannot be
          undone.
        </Typography.Text>
      </Modal>
    </div>
  );
};

export default CacheView;
